import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  BookOpen, Clock, CheckCircle, PlayCircle, Award, ArrowRight, X, ShoppingCart
} from 'lucide-react';
import { mockData } from '../mockData';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const OnlineCourses = () => {
  const navigate = useNavigate();
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [selectedModule, setSelectedModule] = useState(null);
  const [moduleProgress, setModuleProgress] = useState({});
  const [quizAnswers, setQuizAnswers] = useState({});
  const [showQuizFeedback, setShowQuizFeedback] = useState(false);
  const [buyingCourseId, setBuyingCourseId] = useState(null);

  const handleBuyCourse = async (course) => {
    setBuyingCourseId(course.id);
    try {
      const response = await axios.post(`${API}/payments/checkout`, {
        lookup_key: course.lookupKey,
        origin_url: window.location.origin
      });
      window.location.href = response.data.checkout_url;
    } catch (error) {
      console.error('Checkout error:', error);
      toast.error('Sorry, we could not start the checkout. Please try again.');
      setBuyingCourseId(null);
    }
  };

  const handleEnquireCourse = (courseTitle) => {
    // Navigate to home page with pre-filled course name in contact form
    navigate('/?course=' + encodeURIComponent(courseTitle) + '&scrollTo=contact');
  };

  const handleModuleComplete = (moduleId) => {
    setModuleProgress({
      ...moduleProgress,
      [moduleId]: true
    });
  };

  const handleQuizAnswer = (questionId, answer) => {
    setQuizAnswers({
      ...quizAnswers,
      [questionId]: answer
    });
    setShowQuizFeedback(true);
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 md:pt-40 md:pb-28 bg-gradient-to-br from-[#1e3a5f] to-[#2d5a8f]">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center text-white">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
              Online Safety Training Courses
            </h1>
            <p className="text-lg md:text-xl text-gray-200 mb-8 leading-relaxed">
              Self-paced professional training with interactive lessons, knowledge checks, and downloadable certificates
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-12">
              <div className="bg-white/10 backdrop-blur-md rounded-lg p-6 border border-white/20">
                <BookOpen className="text-white mb-3 mx-auto" size={32} />
                <h3 className="font-bold text-lg mb-2">Self-Paced Learning</h3>
                <p className="text-sm text-gray-200">Complete at your own speed</p>
              </div>
              <div className="bg-white/10 backdrop-blur-md rounded-lg p-6 border border-white/20">
                <Award className="text-white mb-3 mx-auto" size={32} />
                <h3 className="font-bold text-lg mb-2">Certificate on Completion</h3>
                <p className="text-sm text-gray-200">Downloadable proof of training</p>
              </div>
              <div className="bg-white/10 backdrop-blur-md rounded-lg p-6 border border-white/20">
                <CheckCircle className="text-white mb-3 mx-auto" size={32} />
                <h3 className="font-bold text-lg mb-2">Industry Accredited</h3>
                <p className="text-sm text-gray-200">Compliant with AS 3745</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Courses Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-[#1e3a5f] mb-4">
              Available Courses
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Choose the course that best fits your training needs
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
            {mockData.onlineCourses.map((course) => (
              <div 
                key={course.id}
                className="bg-white rounded-lg shadow-lg overflow-hidden border-2 border-gray-100 hover:border-[#dc2626] transition-all duration-300 transform hover:-translate-y-2"
              >
                <div className="bg-gradient-to-br from-[#1e3a5f] to-[#2d5a8f] p-8 text-white">
                  <h3 className="text-2xl font-bold mb-2">{course.title}</h3>
                  <div className="flex items-center gap-2 text-gray-200 mb-4">
                    <Clock size={16} />
                    <span className="text-sm">{course.duration}</span>
                  </div>
                  <div className="text-4xl font-bold mb-2">
                    ${course.price}
                    <span className="text-lg font-normal text-gray-200"> AUD</span>
                  </div>
                </div>

                <div className="p-8">
                  <p className="text-gray-600 mb-6 leading-relaxed">
                    {course.description}
                  </p>

                  <div className="mb-6">
                    <h4 className="font-bold text-[#1e3a5f] mb-4 flex items-center gap-2">
                      <BookOpen size={20} />
                      Course Modules ({course.modules.length})
                    </h4>
                    <div className="space-y-3">
                      {course.modules.map((module) => (
                        <div 
                          key={module.id}
                          className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"
                          onClick={() => {
                            setSelectedCourse(course);
                            setSelectedModule(module);
                          }}
                        >
                          <PlayCircle className="text-[#dc2626] flex-shrink-0 mt-0.5" size={20} />
                          <div className="flex-1">
                            <p className="font-semibold text-sm text-[#1e3a5f]">
                              Module {module.id}: {module.title}
                            </p>
                            <p className="text-xs text-gray-500 mt-1">
                              {module.duration}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
                    <h4 className="font-bold text-[#1e3a5f] mb-2 text-sm">Includes:</h4>
                    <ul className="text-sm text-gray-700 space-y-1">
                      <li className="flex items-center gap-2">
                        <CheckCircle size={16} className="text-green-600" />
                        Interactive knowledge checks per module
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle size={16} className="text-green-600" />
                        Final assessment (80% pass mark)
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle size={16} className="text-green-600" />
                        Video training content
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle size={16} className="text-green-600" />
                        Downloadable certificate
                      </li>
                    </ul>
                  </div>

                  <Button
                    onClick={() => handleBuyCourse(course)}
                    disabled={buyingCourseId === course.id}
                    data-testid={`buy-course-btn-${course.id}`}
                    className="w-full bg-[#dc2626] hover:bg-[#b91c1c] text-white py-6 text-lg font-semibold shadow-md hover:shadow-lg transition-all duration-300 disabled:opacity-60"
                  >
                    {buyingCourseId === course.id ? 'Redirecting to Checkout...' : `Buy Now — $${course.price} AUD`}
                    <ShoppingCart className="ml-2" size={20} />
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => handleEnquireCourse(course.title)}
                    data-testid={`enquire-course-btn-${course.id}`}
                    className="w-full mt-3 border-[#1e3a5f] text-[#1e3a5f] hover:bg-[#1e3a5f] hover:text-white py-5 font-semibold transition-all duration-300"
                  >
                    Enquire About This Course
                    <ArrowRight className="ml-2" size={18} />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Module Preview Dialog */}
      <Dialog open={!!selectedModule} onOpenChange={() => setSelectedModule(null)}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl text-[#1e3a5f] flex items-center justify-between">
              <span>Module {selectedModule?.id}: {selectedModule?.title}</span>
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => setSelectedModule(null)}
              >
                <X size={20} />
              </Button>
            </DialogTitle>
          </DialogHeader>

          {selectedModule && (
            <div className="space-y-6 mt-4">
              {/* Video Placeholder */}
              <div className="bg-gray-900 rounded-lg aspect-video flex items-center justify-center">
                <div className="text-center text-white">
                  <PlayCircle size={64} className="mx-auto mb-4 opacity-50" />
                  <p className="text-lg font-semibold">Training Video</p>
                  <p className="text-sm text-gray-400 mt-2">Video content will be available here</p>
                </div>
              </div>

              {/* Module Content */}
              <div className="bg-gray-50 p-6 rounded-lg">
                <h3 className="font-bold text-lg text-[#1e3a5f] mb-3">Module Overview</h3>
                <p className="text-gray-700 leading-relaxed mb-4">
                  {selectedModule.description}
                </p>
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <Clock size={16} />
                  <span>Duration: {selectedModule.duration}</span>
                </div>
              </div>

              {/* Knowledge Check */}
              <div className="bg-white border-2 border-[#1e3a5f] p-6 rounded-lg">
                <h3 className="font-bold text-lg text-[#1e3a5f] mb-4 flex items-center gap-2">
                  <CheckCircle size={24} />
                  Knowledge Check
                </h3>
                
                <div className="space-y-4">
                  <div>
                    <p className="font-semibold text-gray-800 mb-3">
                      Sample Question: What is the primary role of an emergency warden?
                    </p>
                    <div className="space-y-2">
                      {[
                        { id: 'a', text: 'To fight fires directly', correct: false },
                        { id: 'b', text: 'To ensure safe evacuation of occupants', correct: true },
                        { id: 'c', text: 'To call emergency services only', correct: false },
                        { id: 'd', text: 'To secure the building perimeter', correct: false }
                      ].map((option) => (
                        <button
                          key={option.id}
                          onClick={() => handleQuizAnswer('q1', option.id)}
                          className={`w-full text-left p-3 rounded-lg border-2 transition-all ${
                            quizAnswers['q1'] === option.id
                              ? option.correct
                                ? 'border-green-500 bg-green-50'
                                : 'border-red-500 bg-red-50'
                              : 'border-gray-200 hover:border-[#1e3a5f] hover:bg-gray-50'
                          }`}
                        >
                          <span className="font-semibold">{option.id.toUpperCase()})</span> {option.text}
                        </button>
                      ))}
                    </div>
                    
                    {showQuizFeedback && quizAnswers['q1'] && (
                      <div className={`mt-4 p-4 rounded-lg ${
                        quizAnswers['q1'] === 'b' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      }`}>
                        {quizAnswers['q1'] === 'b' 
                          ? '✓ Correct! The primary role is to ensure safe evacuation of all occupants.'
                          : '✗ Incorrect. The correct answer is B - ensuring safe evacuation is the primary responsibility.'}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="flex justify-between items-center pt-4">
                <Button
                  variant="outline"
                  onClick={() => setSelectedModule(null)}
                >
                  Close Preview
                </Button>
                <Button
                  onClick={() => {
                    handleModuleComplete(selectedModule.id);
                    setSelectedModule(null);
                  }}
                  className="bg-[#dc2626] hover:bg-[#b91c1c]"
                >
                  Mark as Complete
                  <CheckCircle className="ml-2" size={18} />
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Footer />
    </div>
  );
};

export default OnlineCourses;
